import styled from "@emotion/styled";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import { Typography } from "@avenue-8/sidekick-ui";
import { CopyIconButton } from "@shared/components/CopyIconButton";

type SuggestedApproachCardSize = "sm" | "md";

type SuggestedApproachCardProps = {
  text: string;
  className?: string;
  // "sm" (default) is the signals page's denser card; "md" is the contact
  // page's roomier one - everything else about the card is shared.
  size?: SuggestedApproachCardSize;
};

const ICON_SIZE_PX: Record<SuggestedApproachCardSize, number> = {
  sm: 16,
  md: 20,
};

const TEXT_VARIANT: Record<SuggestedApproachCardSize, "body1" | "body2"> = {
  sm: "body2",
  md: "body1",
};

export const SuggestedApproachCard = ({
  text,
  className,
  size = "sm",
}: SuggestedApproachCardProps) => (
  <Root className={className}>
    <PositionedCopyIconButton text={text} ariaLabel="Copy suggested approach" />
    <Header>
      <IconWrap>
        <TipsAndUpdatesIcon sx={{ fontSize: ICON_SIZE_PX[size] }} />
      </IconWrap>
      <Label variant="subtitle2">Suggested approach</Label>
    </Header>
    <Typography variant={TEXT_VARIANT[size]}>{text}</Typography>
  </Root>
);

const Root = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.875rem 1rem;
  padding-right: 2.25rem;
  border-radius: 0.5rem;
  background-color: ${(p) => p.theme.text.colors.brand}1a;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

const IconWrap = styled.span`
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.text.colors.brand};
`;

const Label = styled(Typography)`
  color: ${(p) => p.theme.text.colors.brand};
  line-height: 1;
`;

// Muted rather than brand-colored - it's a utility action, not the focal
// point of the card.
const PositionedCopyIconButton = styled(CopyIconButton)`
  position: absolute;
  top: 0.375rem;
  right: 0.375rem;
  color: ${(p) => p.theme.text.colors.secondary};
`;
